/**
 * The unread count behind the header bell (tracker item 62). One poller for the
 * whole layout (AppLayout owns it and hands the result to each bell): every
 * 60 s while the tab is visible, on every route change, when the tab comes
 * back, and when the lists report a change. Only ADMIN / OFFICE_MANAGER make
 * a request; a failed call keeps the last number (the server's gate is the
 * real check).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createAPIClient, IStaffNotificationsApi } from '../../api';
import * as types from '../../api/types';
import { canViewStaffNotifications } from '../../utils/permissions';
import { STAFF_NOTIFICATIONS_CHANGED } from '../../utils/staffNotifications';

export const STAFF_NOTIFICATIONS_POLL_MS = 60_000;

export interface StaffNotificationCount {
  visible: boolean;
  unread: number;
  capped: boolean;
  /** Reload the count now */
  refresh: () => void;
}

export function useStaffNotificationCount(user: types.User | null, pathname: string): StaffNotificationCount {
  const api = useMemo<IStaffNotificationsApi>(() => createAPIClient(), []);
  const visible = canViewStaffNotifications(user);
  const [state, setState] = useState({ unread: 0, capped: false });
  const active = useRef(true);

  const refresh = useCallback(() => {
    if (!visible) return;
    void (async () => {
      try {
        const count = await api.getStaffNotificationUnreadCount();
        if (active.current) setState({ unread: count.unread, capped: count.capped });
      } catch {
        // Keep the last number
      }
    })();
  }, [api, visible]);

  useEffect(() => {
    active.current = true;
    if (!visible) {
      setState({ unread: 0, capped: false });
      return () => { active.current = false; };
    }
    refresh();
    const tickIfVisible = () => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') refresh();
    };
    const timer = window.setInterval(tickIfVisible, STAFF_NOTIFICATIONS_POLL_MS);
    window.addEventListener(STAFF_NOTIFICATIONS_CHANGED, refresh);
    document.addEventListener('visibilitychange', tickIfVisible);
    return () => {
      active.current = false;
      window.clearInterval(timer);
      window.removeEventListener(STAFF_NOTIFICATIONS_CHANGED, refresh);
      document.removeEventListener('visibilitychange', tickIfVisible);
    };
    // a route change reloads too (pathname)
  }, [visible, refresh, pathname]);

  return { visible, unread: state.unread, capped: state.capped, refresh };
}
